import { compareSyncCursors } from './coverage';
import type { MessageId, MessageSyncEvent, SortOrder, SyncCursor, SyncEvent } from './types';

// Keeps fetch-race replay cheap while covering bursts far larger than one viewport.
const RECENT_MESSAGE_EVENT_RING_SIZE = 256;

/** Message sync event retained briefly so page fetches can replay races after their snapshot cursor. */
export interface RecentMessageEvent {
  readonly cursor: SyncCursor;
  readonly event: MessageSyncEvent;
}

/** Retains the newest message cursors in replay order, ignoring entities and duplicate object references. */
export function rememberRecentMessageEvent(events: RecentMessageEvent[], event: SyncEvent): void {
  if (event.type === 'entity.updated') return;
  if (events.some((entry) => entry.event === event)) return;

  const previousCursor = events.at(-1)?.cursor;
  events.push({ cursor: event.cursor, event });
  // A historical sync batch can arrive after a newer live event. Retain by
  // cursor rather than arrival order so that batch cannot evict the live race.
  if (previousCursor !== undefined && compareSyncCursors(event.cursor, previousCursor) < 0) {
    events.sort((left, right) => compareSyncCursors(left.cursor, right.cursor));
  }
  if (events.length > RECENT_MESSAGE_EVENT_RING_SIZE) {
    events.splice(0, events.length - RECENT_MESSAGE_EVENT_RING_SIZE);
  }
}

/** Filters replay-ring events newer than a snapshot cursor and optionally scoped to inserted ids. */
export function replayableRecentMessageEvents(
  events: readonly RecentMessageEvent[],
  snapshotCursor: SyncCursor | undefined,
  messageIds?: ReadonlySet<MessageId>,
  sortOrders?: ReadonlySet<SortOrder>
): RecentMessageEvent[] {
  return events.filter(({ cursor, event }) => (
    isReplayCursorAfterSnapshot(cursor, snapshotCursor) &&
    (messageIds === undefined ||
      (event.messageId !== undefined && messageIds.has(event.messageId)) ||
      (event.type === 'message.deleted' && sortOrders?.has(event.sortOrder) === true))
  ));
}

function isReplayCursorAfterSnapshot(cursor: SyncCursor, snapshotCursor: SyncCursor | undefined): boolean {
  if (cursor.length === 0) return false;
  if (snapshotCursor === undefined) return true;
  return compareSyncCursors(cursor, snapshotCursor) > 0;
}
